import React, { FC, useEffect, useState } from 'react'
import { Autocomplete, FormControl, Grid, InputLabel, TextField, Theme } from '@mui/material'
import { Controller, FieldValues, FieldPath, RegisterOptions } from 'react-hook-form'
import { makeStyles } from '@mui/styles'
import type { BaseSelectWithSearchProps, SelectWithSearchProps } from './SelectWithSearch.types'
import { SelectArrow } from '../SelectArrow'

type StyleProps = BaseSelectWithSearchProps

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: ({ width = 300 }: StyleProps) => width,
    height: 63,
    marginBottom: ({ mb = 25 }: StyleProps) => mb,
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  formControl: {
    width: ({ width = 300 }: StyleProps) => width,
  },
  input: {
    height: 40,
    '& .MuiOutlinedInput-root': {
      paddingRight: 0,
    },
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  chip: {
    color: theme.palette.primary.light,
  },
  loading: {
    color: theme.palette.primary.light,
  },
}))

export const SelectWithSearch = <T extends FieldValues, TName extends FieldPath<T>>({
  inputName,
  labelName,
  selectedId,
  placeholder,
  width,
  mb,
  noOptionsText,
  control,
  loading,
  options,
  onSearch,
  defaultOption,
  filterOption,
  rules,
}: SelectWithSearchProps<T, TName> & { rules?: RegisterOptions }): ReturnType<FC<SelectWithSearchProps<T, TName>>> => {
  const classes = useStyles({ width, mb })
  const [search, setSearch] = useState<string>('')

  useEffect(() => {
    if (search.length > 1) onSearch(search)
  }, [search, onSearch])

  useEffect(() => {
    onSearch(search)
  }, []) // eslint-disable-line

  return (
    <Grid container className={classes.container}>
      <Grid item>
        <InputLabel shrink htmlFor={inputName} className={classes.label}>
          {labelName}
        </InputLabel>
      </Grid>
      <FormControl className={classes.formControl}>
        <Controller
          name={inputName}
          control={control}
          rules={rules}
          render={({ field, fieldState }) => (
            <Autocomplete
              options={options}
              noOptionsText={noOptionsText}
              classes={{
                noOptions: classes.noOptions,
                loading: classes.loading,
              }}
              clearIcon={null}
              popupIcon={<SelectArrow />}
              getOptionLabel={(option) => option.label}
              getOptionKey={(option) => option.value}
              loading={loading}
              value={options.find((u) => u.value === selectedId) || defaultOption || null}
              inputValue={search !== '' ? search : defaultOption?.label || ''}
              onOpen={() => {
                // Reset search state and fetch full list when dropdown opens
                setSearch('')
                onSearch('')
              }}
              onInputChange={(_, value, reason) => {
                if (reason === 'input') {
                  setSearch(value)
                }
              }}
              onChange={(_, newValue) => {
                if (newValue) {
                  field.onChange(newValue.value)
                  setSearch(newValue.label)
                } else {
                  field.onChange()
                  setSearch('')
                }
              }}
              filterOptions={filterOption}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder={placeholder}
                  error={!!fieldState.error}
                  InputProps={{
                    ...params.InputProps,
                    className: classes.input,
                  }}
                />
              )}
            />
          )}
        />
      </FormControl>
    </Grid>
  )
}
